import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { getAuditLogs } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Select from '@/components/common/Select';
import Modal from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDateTime, formatStatus } from '@/utils/formatters';
import type { AuditLog } from '@/types/admin';

export default function AuditLogs() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['auditLogs', page, actionFilter, entityFilter],
    queryFn: () => getAuditLogs(page, 50, actionFilter || undefined, entityFilter || undefined),
  });

  const columns = useMemo<ColumnDef<AuditLog>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Time',
        cell: ({ row }) => formatDateTime(row.original.created_at),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <Badge variant="info">{formatStatus(row.original.action)}</Badge>
        ),
      },
      {
        accessorKey: 'entity_type',
        header: 'Entity Type',
        cell: ({ row }) => (
          <span className="capitalize">{row.original.entity_type}</span>
        ),
      },
      {
        accessorKey: 'entity_id',
        header: 'Entity ID',
        cell: ({ row }) =>
          row.original.entity_id ? (
            <span className="font-mono text-sm text-gray-500">
              {row.original.entity_id.slice(0, 8)}...
            </span>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'ip_address',
        header: 'IP Address',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.ip_address || '-'}</span>
        ),
      },
      {
        id: 'details',
        header: '',
        cell: ({ row }) => (
          <button
            onClick={() => setSelectedLog(row.original)}
            className="text-brand hover:text-brand-dark text-sm font-medium"
          >
            Details
          </button>
        ),
      },
    ],
    []
  );

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'suspend_user', label: 'Suspend User' },
    { value: 'ban_user', label: 'Ban User' },
    { value: 'approve_business', label: 'Approve Business' },
    { value: 'reject_business', label: 'Reject Business' },
    { value: 'refund_payment', label: 'Refund Payment' },
    { value: 'resolve_conversation_flag', label: 'Resolve Conversation Flag' },
    { value: 'resolve_review_flag', label: 'Resolve Review Flag' },
    { value: 'issue_violation', label: 'Issue Violation' },
    { value: 'export_data', label: 'Export Data' },
  ];

  const entityOptions = [
    { value: '', label: 'All Entities' },
    { value: 'user', label: 'User' },
    { value: 'business', label: 'Business' },
    { value: 'payment', label: 'Payment' },
    { value: 'conversation_flag', label: 'Conversation Flag' },
    { value: 'review_flag', label: 'Review Flag' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="mt-1 text-sm text-gray-500">
          View all admin actions and system events
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={actionFilter}
              onChange={setActionFilter}
              options={actionOptions}
              className="w-full sm:w-48"
            />
            <Select
              value={entityFilter}
              onChange={setEntityFilter}
              options={entityOptions}
              className="w-full sm:w-48"
            />
          </div>
        </CardBody>
      </Card>

      {/* Logs table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.logs || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={50}
            totalCount={data?.total}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Log Detail Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Audit Log Details"
        size="lg"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Action</p>
                <p className="text-gray-900">{formatStatus(selectedLog.action)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Entity Type</p>
                <p className="text-gray-900 capitalize">{selectedLog.entity_type}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Entity ID</p>
                <p className="font-mono text-sm text-gray-900">
                  {selectedLog.entity_id || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">IP Address</p>
                <p className="font-mono text-sm text-gray-900">
                  {selectedLog.ip_address || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Time</p>
                <p className="text-gray-900">{formatDateTime(selectedLog.created_at)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Admin User ID</p>
                <p className="font-mono text-sm text-gray-900">
                  {selectedLog.admin_user_id || 'System'}
                </p>
              </div>
            </div>

            {selectedLog.old_values && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Previous Values</p>
                <pre className="p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(selectedLog.old_values, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.new_values && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">New Values</p>
                <pre className="p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(selectedLog.new_values, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
